import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, computed, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ciInterface, ciInterface2 } from 'src/app/util/interfaces/ci';
import { faAngleDown, faExternalLinkAlt, faSquarePen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CmdbSearchGadgetComponent } from 'src/app/page_component/cmdb-search-gadget/cmdb-search-gadget.component';
import { requestInterface } from 'src/app/util/interfaces/request';
import { ModalComponent } from 'src/app/page_component_item/modal/modal.component';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { RequestPageRelatedCiService } from './data/request-page-related-ci.service';
import { GenericFormGenModule } from 'src/app/page_component_item/generic-form-gen/generic-form-gen.module';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';
import { CmdbRegisterPageInfoService } from 'src/app/page/cmdb/cmdb-register-page/components/cmdb-register-page-info/data/cmdb-register-page-info.service';
import { RequestPageService } from '../../../service/request-page.service';
import { logInterface } from 'src/app/util/interfaces/log.interface';
import { ShkDate } from 'shk-date';
import { forkJoin } from 'rxjs';

@Component({
	selector: 'pcp-request-page-tab-related-ci',
	standalone: true,
	imports: [CommonModule, FormsModule, FontAwesomeModule, CmdbSearchGadgetComponent, RouterModule, ModalComponent, GenericFormGenModule],
	templateUrl: './request-page-tab-related-ci.component.html',
	styleUrl: './request-page-tab-related-ci.component.scss',
})
export class RequestPageTabRelatedCiComponent implements OnInit {
	private readonly labelsService = inject(LabelsService);
	private readonly notificationService = inject(NotificationService);
	private readonly requestPageRelatedCisService = inject(RequestPageRelatedCiService);
	private readonly cmdbRegisterPageInfoService = inject(CmdbRegisterPageInfoService);
	private readonly requestPageService = inject(RequestPageService);
	@ViewChild('confirmCiBondModal') confirmCiBondModal: ModalComponent;
	@ViewChild('relatedIncidentModal') relatedIncidentModal: ModalComponent;
	@ViewChild('editCiFieldModal') editCiFieldModal: ModalComponent;

	icons = {
		faAngleDown: faAngleDown,
		faExternalLinkAlt: faExternalLinkAlt,
		faSquarePen: faSquarePen,
		faTrash: faTrash,
	};
	labels: LabelsInterface;

	constructor() {
		this.labels = computed(() => {
			return this.labelsService.labels();
		})();
	}

	newCi: ciInterface = {
		ciType: '',
		ciIcon: '',
		ciArray: [
			{
				id: '',
				cmdb_id: '',
				status: '',
				type: '',
				identification: '',
				id_solution_area: [''],
			},
		],
	};

	bondedCisIdArr: string[] = [];
	searchSelectedCi: ciInterface2;

	@Input() requestData: requestInterface;
	@Output() requestDataChange = new EventEmitter<requestInterface>();
	@Input() requestDataMemory: requestInterface;
	relatedIncident: requestInterface[] = [];

	isLoadingIncidentDiscovery = false;
	isLoadingCiFields = false;
	isSavingCiField = false;
	editingCi: ciInterface2;
	ciFields: genericFormI[] = [];
	selectedCiField: genericFormI;
	selectedCiFieldOriginalValues: unknown[] = [];
	ciFieldSearch = '';

	ngOnInit(): void {
		this.bondedCisIdArr = this.requestData.relatedCis?.map((ci) => ci.id) || [];
	}

	get filteredCiFields(): genericFormI[] {
		const search = this.ciFieldSearch.trim().toLowerCase();
		const visibleFields = this.ciFields.filter((field) => !field.hidden && !field.disabled);
		if (!search) return visibleFields;
		return visibleFields.filter((field) => `${field.label} ${field.name}`.toLowerCase().includes(search));
	}

	openCiFieldEditor(ci: ciInterface2): void {
		this.editingCi = ci;
		this.ciFieldSearch = '';
		this.selectedCiField = undefined;
		this.selectedCiFieldOriginalValues = [];
		this.ciFields = [];
		this.isLoadingCiFields = true;
		this.editCiFieldModal.openModal();

		forkJoin({
			form: this.cmdbRegisterPageInfoService.getRegisterForm(String(ci.id_type), ci.id),
			data: this.cmdbRegisterPageInfoService.getCiData(ci.id, new ShkDate()),
		}).subscribe({
			next: ({ form, data }) => {
				this.ciFields = this.populateCiFields(form, data);
			},
			error: () => {
				this.isLoadingCiFields = false;
				this.notificationService.setNotificationMessage('Não foi possível carregar os campos do CI.');
				this.editCiFieldModal.closeModal();
			},
			complete: () => (this.isLoadingCiFields = false),
		});
	}

	private populateCiFields(fields: genericFormI[], ciData: ciInterface2): genericFormI[] {
		return fields.map((field) => {
			if (!Object.prototype.hasOwnProperty.call(ciData, field.name)) return field;

			const currentValue = (ciData as Record<string, any>)[field.name];
			if (field.inputType === 'multi-select') {
				const selectedValues = Array.isArray(currentValue) ? currentValue : [];
				field.values = (field.items || []).filter((item) => selectedValues.includes(item.values[0]));
			} else if (field.inputType === 'multi-input') {
				try {
					field.values = JSON.parse(currentValue);
				} catch {
					field.values = currentValue;
				}
			} else {
				field.values = Array.isArray(currentValue) ? currentValue : [currentValue];
			}

			field.rawValues = JSON.parse(JSON.stringify(field.values));
			return field;
		});
	}

	selectCiField(field: genericFormI): void {
		this.selectedCiField = field;
		this.selectedCiFieldOriginalValues = structuredClone(field.values || []);
	}

	private translateItemLabel(item: genericFormI): string {
		return this.labels[item.label] || item.label || item.name || '';
	}

	// Resolve o rótulo traduzido de uma opção (ex.: select de localidade) a partir do valor bruto salvo
	private resolveFieldDisplayValue(field: genericFormI, rawValue: unknown): string {
		if (rawValue === undefined || rawValue === null || rawValue === '') return '';
		if (typeof rawValue === 'object') return this.translateItemLabel(rawValue as genericFormI);

		const rawValueText = rawValue.toString();
		const matchingItem = (field.items || []).find((item) => item.values?.[0]?.toString() === rawValueText);
		return matchingItem ? this.translateItemLabel(matchingItem) : rawValueText;
	}

	private resolveFieldDisplayValues(field: genericFormI, values: unknown[]): string {
		return (values || []).map((value) => this.resolveFieldDisplayValue(field, value)).join(', ');
	}

	saveCiField(): void {
		if (!this.editingCi || !this.selectedCiField || this.isSavingCiField) return;

		const oldValue = this.resolveFieldDisplayValues(this.selectedCiField, this.selectedCiFieldOriginalValues);
		const newValue = this.resolveFieldDisplayValues(this.selectedCiField, this.selectedCiField.values);
		if (oldValue === newValue) {
			this.notificationService.setNotificationMessage('Nenhuma alteração foi realizada.');
			return;
		}

		const fieldLabel = this.labels[this.selectedCiField.label] || this.selectedCiField.label || this.selectedCiField.name;

		this.isSavingCiField = true;
		this.cmdbRegisterPageInfoService.updateCiWithGeneric(this.ciFields, this.editingCi.id).subscribe({
			next: () => {
				const log: logInterface = {
					idLog: '',
					log_date: '',
					analyst: '',
					notes: `CI ${this.editingCi.identification} atualizado: ${fieldLabel} de "${oldValue}" para "${newValue}".`,
					// status neutro para não reprocessar a máquina de status do chamado
					status: 'Custom Field Update',
					duration: '00:01',
				};
				this.requestPageService.addLog(this.requestData.id, log).subscribe({
					next: (logResponse: logInterface) => {
						this.requestData.logs.unshift({ ...log, ...logResponse });
						this.notificationService.setNotificationMessage('CI atualizado e acompanhamento registrado com sucesso.');
						this.editCiFieldModal.closeModal();
					},
					error: () => {
						this.isSavingCiField = false;
						this.notificationService.setNotificationMessage('CI atualizado, mas não foi possível registrar o acompanhamento.');
					},
					complete: () => (this.isSavingCiField = false),
				});
			},
			error: () => {
				this.isSavingCiField = false;
				this.notificationService.setNotificationMessage('Você não possui permissão para editar este CI ou a atualização falhou.');
			},
		});
	}

	bondCi(ci: ciInterface2): void {
		if (this.bondedCisIdArr.includes(ci.id)) {
			this.notificationService.setNotificationMessage(this.labels.lbl_vinculou_ci_existente);
			return;
		}

		this.searchSelectedCi = ci;

		this.requestData.relatedCis.push(this.searchSelectedCi);
		this.notificationService.setNotificationMessage(this.labels.lbl_adicionado_com_sucesso);

		this.requestPageRelatedCisService
			.addCiToRequest(this.requestData.id, this.searchSelectedCi.id)
			.subscribe({
				next: () => {
					this.bondedCisIdArr.push(this.searchSelectedCi.id);

					if (this.requestDataMemory.service_type == 'Incident') {
						this.requestPageRelatedCisService
							.getIncidentDiscovery(
								this.requestDataMemory.id_scope,
								this.requestDataMemory.id_category,
								this.requestDataMemory.id_topic,
								this.requestDataMemory.id_solution_area,
								this.requestData.relatedCis.map((relatedCis) => {
									return relatedCis.id;
								})
							)
							.subscribe({
								next: (relatedIncidentRes) => {
									this.relatedIncident = relatedIncidentRes;
									if (this.relatedIncident.length > 0) {
										this.relatedIncidentModal.openModal();
									}
								},
							})
							.add(() => {
								this.isLoadingIncidentDiscovery = false;
							});
					}
				},
			})
			.add(() => {
				this.searchSelectedCi = null;
			});
	}

	removeCi(ci: ciInterface2): void {
		if (this.requestData.mandatoryCiBinding && this.requestData.relatedCis?.length == 1) {
			this.notificationService.setNotificationMessage(this.labels.lbl_vincular_pelo_menos_um_ci);
			return;
		}
		for (const [index, ciLoop] of this.requestData.relatedCis.entries()) {
			if (ciLoop.id === ci.id) {
				this.requestData.relatedCis.splice(index, 1);
			}
		}
		this.notificationService.setNotificationMessage(this.labels.lbl_removido_com_sucesso);

		this.requestPageRelatedCisService.removeCiFromRequest(this.requestData.id, ci.id).subscribe(() => {});
	}

	setRequestParent(idRequestParent: string): void {
		this.requestData.parent_request.id = idRequestParent;
		this.requestDataChange.emit(this.requestData);
	}

	confirmButton(elementReference: HTMLElement): void {
		this.notificationService.setNotificationMessage(this.labels.lbl_clique_confirmar_acao);
		elementReference.classList.add('confirmClick');
		setTimeout(() => {
			elementReference.classList.remove('confirmClick');
		}, 5000);
	}
}
